import { Component } from '@theme/component';

const ITEM_SELECTOR = '.nav-new__item';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

/**
 * Delay before a hovered-away panel closes. Long enough that moving the pointer
 * between the link and the panel, or tabbing from the link into the panel,
 * does not close it; short enough not to feel sticky.
 */
const CLOSE_DELAY = 120;

/** Breathing room kept between a panel's right edge and the viewport edge. */
const PANEL_VIEWPORT_MARGIN = 16;

/**
 * Resolves the `<li>` a delegated event belongs to.
 *
 * `component.js` proxies the event so `target` is the element carrying the
 * `on:*` attribute, which is the `<li>` itself — `closest` then returns it
 * unchanged. The lookup is kept so the handlers still work if the attributes
 * are ever moved onto a descendant.
 *
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function itemFrom(event) {
  const { target } = event;

  if (!(target instanceof Element)) return null;

  const item = target.closest(ITEM_SELECTOR);

  return item instanceof HTMLElement ? item : null;
}

/**
 * Desktop dropdown navigation for the "New" menu design.
 *
 * Open state lives entirely in the `data-open` attribute on each `<li>`, so a
 * Section Rendering API morph simply resets every panel to closed rather than
 * leaving the component and the DOM disagreeing.
 *
 * @extends {Component<{}>}
 */
class NavNewMenu extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('keyup', this.#onKeyUp, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#clearTimer();
  }

  /**
   * Opens the hovered or focused item. Bound from markup via
   * `on:pointerenter` and `on:focus`.
   *
   * @param {Event} event
   */
  activate(event) {
    const item = itemFrom(event);

    if (!item) return;

    this.#clearTimer();
    this.#open(item);
  }

  /**
   * Closes the item the pointer or focus just left, after a short grace period.
   * Bound from markup via `on:pointerleave` and `on:blur`.
   *
   * @param {Event} event
   */
  deactivate(event) {
    const item = itemFrom(event);

    if (!item) return;

    this.#clearTimer();
    this.#closeTimer = setTimeout(() => this.#close(item), CLOSE_DELAY);
  }

  /**
   * Handles a click on a parent link.
   *
   * With a fine pointer the panel is already open from hover, so the click is
   * left alone and follows the link. Without one — touch, or a tablet — the
   * first tap opens the panel and a second tap follows the link.
   *
   * @param {Event} event
   */
  toggle(event) {
    if (window.matchMedia(FINE_POINTER).matches) return;

    const item = itemFrom(event);

    if (!item || item.dataset.open === 'true') return;

    event.preventDefault();
    this.#closeAll();
    this.#open(item);
  }

  /**
   * @param {HTMLElement} item
   */
  #open(item) {
    for (const other of this.#items) {
      if (other !== item) this.#close(other);
    }

    item.dataset.open = 'true';
    item.querySelector('[aria-haspopup]')?.setAttribute('aria-expanded', 'true');

    this.#keepInViewport(item);
  }

  /**
   * Nudges a panel back inside the viewport if opening it would push its right
   * edge off screen — which happens to the last links in the bar on narrower
   * desktops, or when the menu carries more items than the design assumed.
   *
   * @param {HTMLElement} item
   */
  #keepInViewport(item) {
    const panel = item.querySelector('[data-nav-new-panel]');

    if (!(panel instanceof HTMLElement)) return;

    panel.style.removeProperty('--nav-new-panel-shift');

    const { right } = panel.getBoundingClientRect();
    const overflow = right - document.documentElement.clientWidth + PANEL_VIEWPORT_MARGIN;

    if (overflow > 0) panel.style.setProperty('--nav-new-panel-shift', `${Math.round(overflow)}px`);
  }

  /**
   * @param {HTMLElement} item
   */
  #close(item) {
    delete item.dataset.open;
    item.querySelector('[aria-haspopup]')?.setAttribute('aria-expanded', 'false');

    const panel = item.querySelector('[data-nav-new-panel]');

    if (panel instanceof HTMLElement) panel.style.removeProperty('--nav-new-panel-shift');
  }

  #closeAll() {
    for (const item of this.#items) this.#close(item);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return Array.from(this.querySelectorAll(ITEM_SELECTOR)).filter(
      /** @returns {element is HTMLElement} */
      (element) => element instanceof HTMLElement
    );
  }

  #clearTimer() {
    if (this.#closeTimer === null) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const open = this.querySelector(`${ITEM_SELECTOR}[data-open='true']`);

    if (!(open instanceof HTMLElement)) return;

    this.#close(open);

    const trigger = open.querySelector('[aria-haspopup]');

    if (trigger instanceof HTMLElement) trigger.focus();
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    const { target } = event;

    if (target instanceof Node && this.contains(target)) return;

    this.#clearTimer();
    this.#closeAll();
  };
}

if (!customElements.get('nav-new-menu')) {
  customElements.define('nav-new-menu', NavNewMenu);
}
